import React from 'react';
import { IIncidentStatus } from '../../../../../core/interfaces/IIncidents';
import { MetricDataSet } from './MetricsDisplay';

interface PaginationProps {
  currentDataSet: MetricDataSet;
  selectedStatus: IIncidentStatus[];
  pageLength: number;
  startingPoint: number;
  paginationStartingPoint: number;
  onPageChange: (page: number, paginationStart?: number) => void;
}

export const PageLengthSelector: React.FC<{
  currentDataSet: MetricDataSet;
  selectedStatus: IIncidentStatus[];
  pageLength: number;
  onPageLengthChange: (length: number) => void;
}> = ({ currentDataSet, selectedStatus, pageLength, onPageLengthChange }) => {
  const totalNumberOfIncidents = selectedStatus.reduce(
    (sum, incidentStatus) => sum + currentDataSet[incidentStatus].current,
    0,
  );

  if (totalNumberOfIncidents < 5) {
    return null;
  }

  const intervals = [5];
  let increment = 5;

  // Generate page length options (5, 10, 15, etc.)
  while (
    intervals.length < 3 &&
    intervals[intervals.length - 1] + increment <= totalNumberOfIncidents
  ) {
    intervals.push(intervals[intervals.length - 1] + increment);
    increment += 5;
  }

  return (
    <div className="float-left" style={{ width: 180 }}>
      <select
        className="select-design"
        value={pageLength}
        onChange={event => {
          onPageLengthChange(parseInt(event.target.value));
        }}
      >
        {intervals.map((interval: number, index: number) => (
          <option key={index} value={interval}>
            {interval} per page
          </option>
        ))}
      </select>
    </div>
  );
};

const Pagination: React.FC<PaginationProps> = ({
  currentDataSet,
  selectedStatus,
  pageLength,
  startingPoint,
  paginationStartingPoint,
  onPageChange,
}) => {
  const totalNumberOfIncidents = selectedStatus.reduce(
    (sum, incidentStatus) => sum + currentDataSet[incidentStatus].current,
    0,
  );

  const numberOfPages = Math.ceil(totalNumberOfIncidents / pageLength);

  if (numberOfPages <= 1) {
    return null;
  }

  const currentPage = Math.floor(startingPoint / pageLength);

  // Generate page numbers
  const pages = [];
  for (let i = paginationStartingPoint, j = 0; i < numberOfPages && j < 5; i++, j++) {
    pages.push(
      <span
        className="page-number item-box-tagline font-bold no-select cursor-pointer"
        onClick={() => onPageChange(i * pageLength)}
        style={currentPage === i ? { backgroundColor: '#f3f4f6' } : {}}
        key={i}
      >
        {i + 1}
      </span>,
    );
  }

  return (
    <div style={{ textAlign: 'center' }} className="pagination-container-div">
      {numberOfPages > 5 && (
        <img
          src="/icons/carousel-double-left.svg"
          className="no-select cursor-pointer"
          alt="left"
          onClick={() => onPageChange(0, 0)}
          style={{
            marginRight: 33,
            verticalAlign: 'middle',
            pointerEvents: currentPage !== 0 ? 'all' : 'none',
            opacity: currentPage !== 0 ? 1 : 0.3,
          }}
        />
      )}

      {numberOfPages > 5 && (
        <img
          className="no-select cursor-pointer"
          alt="previous"
          onClick={() => {
            const newPaginationStartingPoint =
              paginationStartingPoint - 1 < 0 ? 0 : paginationStartingPoint - 1;

            onPageChange(startingPoint - pageLength, newPaginationStartingPoint);
          }}
          src="/icons/arrow-back.svg"
          style={{
            width: 14,
            height: 14,
            position: 'relative',
            top: 2,
            marginRight: 33,
            pointerEvents: paginationStartingPoint > 0 ? 'all' : 'none',
            opacity: paginationStartingPoint > 0 ? 1 : 0.3,
          }}
        />
      )}

      {pages}

      {numberOfPages > 5 && (
        <img
          className="no-select cursor-pointer"
          alt="next"
          onClick={() => {
            const newPaginationStartingPoint =
              paginationStartingPoint + 1 === numberOfPages
                ? numberOfPages - 1
                : paginationStartingPoint + 1;

            onPageChange(startingPoint + pageLength, newPaginationStartingPoint);
          }}
          src="/icons/arrow-forward.svg"
          style={{
            width: 14,
            height: 14,
            position: 'relative',
            top: 2,
            marginLeft: 33,
            pointerEvents: paginationStartingPoint + 5 <= numberOfPages - 1 ? 'all' : 'none',
            opacity: paginationStartingPoint + 5 <= numberOfPages - 1 ? 1 : 0.3,
          }}
        />
      )}

      {numberOfPages > 5 && (
        <img
          src="/icons/carousel-double-right.svg"
          alt="last"
          className="no-select cursor-pointer"
          onClick={() => onPageChange((numberOfPages - 1) * pageLength, numberOfPages - 5)}
          style={{
            marginLeft: 33,
            verticalAlign: 'middle',
            pointerEvents: currentPage !== numberOfPages - 1 ? 'all' : 'none',
            opacity: currentPage !== numberOfPages - 1 ? 1 : 0.3,
          }}
        />
      )}
    </div>
  );
};

export default Pagination;
